import { requiresExpiryGrouping } from '@/store/option/selectors/optionSelectors.ts';
import type { HedgeType, OptionLegProperties } from '@/store/option/state.ts';

export type StrategyExpiryGroup = {
  expiryDate: string;
  legs: OptionLegProperties[];
  isOpen: boolean;
  canGroupHedges: boolean;
  groupColumn: number;
  columnSpan: number;
};

type StrategyGroupingInput = {
  legs: OptionLegProperties[];
  hedgeType: HedgeType;
  groupedByExpiry: boolean;
  collapsedExpiries: ReadonlySet<string>;
};

/** Keep leg order and place each group directly after the preceding visible columns. */
export function getStrategySections ({ legs, hedgeType, groupedByExpiry, collapsedExpiries }: StrategyGroupingInput) {
  const expiryGroups = Map.groupBy(legs, (leg) => (groupedByExpiry ? (leg.expiryDate ?? 'No expiry') : ''));

  return expiryGroups.entries().reduce<StrategyExpiryGroup[]>((groups, [expiryDate, groupLegs]) => {
    // A single expiry or single leg cannot be collapsed; new expiries default to open.
    const isOpen = expiryGroups.size === 1 || groupLegs.length === 1 || !collapsedExpiries.has(expiryDate);
    const previousGroup = groups.at(-1);
    const groupColumn = previousGroup === undefined ? 2 : previousGroup.groupColumn + previousGroup.columnSpan;

    return [
      ...groups,
      {
        expiryDate,
        legs: groupLegs,
        isOpen,
        canGroupHedges: canShareHedges(hedgeType, groupLegs),
        groupColumn,
        columnSpan: isOpen ? groupLegs.length : 1,
      },
    ];
  }, []);
}

/** Forward and Ndf groups share editors only with multiple legs and a defined, common delivery date. */
function canShareHedges(hedgeType: HedgeType, legs: OptionLegProperties[]) {
  const deliveryDate = legs[0]?.deliveryDate;
  return (
    requiresExpiryGrouping(hedgeType) &&
    legs.length > 1 &&
    deliveryDate !== undefined &&
    legs.every((leg) => leg.deliveryDate === deliveryDate)
  );
}
